/**
 * Virtual Tenant Attribution — v32.2 customer-gap fill
 * ====================================================
 * Per-department rollup of a shared LLM endpoint's spend. Sends
 * X-Cartie-Tenant header from the proxy, then this page splits the
 * shared bill into a P&L by department.
 *
 * Brand: dark marketing page (free-tools-tier).
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Link } from 'react-router-dom';
// v34.55 — promoted from BackToHomePill → PublicNavbar so visitors get the same top nav as /pilot, /wall-of-tenants, etc.
import PublicNavbar from '../components/PublicNavbar';
import { useAuth } from '../context/AuthContext';
import {
  Users, RefreshCcw, Sparkles, ArrowRight, Loader2,
  Database, PieChart as PieIcon, ListChecks, Code2,
} from 'lucide-react';
import { ENGINE_COUNT } from '../constants/product';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TENANT_COLORS = [
  '#10b981', // emerald-500
  '#14b8a6', // teal-500
  '#f59e0b', // amber-500
  '#6366f1', // indigo-500
  '#a855f7', // purple-500
  '#ec4899', // pink-500
  '#0ea5e9', // sky-500
];

const SAMPLE_CURL = `# Just pass one header on every proxy call:
curl -X POST https://proxy.cartieai.com/v1/chat/completions \\
  -H "Authorization: Bearer $CARTIE_KEY" \\
  -H "X-Cartie-Tenant: marketing" \\        # ← attribution
  -d '{"model":"gpt-5.2","messages":[...]}'

# That's it. CARTIE splits the shared bill into a per-department P&L.`;

const PROXY_WIRED_BADGE = (
  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 bg-emerald-500/10">
    ● Live · wired into /api/llm-proxy
  </span>
);

const VirtualTenant = () => {
  const { user } = useAuth(); // v35.69 — layout provides authed Navbar; avoid double nav
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/virtual-tenant/breakdown`);
      setData(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API_URL}/virtual-tenant/seed-demo`);
      await load();
    } finally { setSeeding(false); }
  };

  useEffect(() => {
    document.title = 'Virtual Tenant Attribution · Split shared model bills by department | CARTIEAI';
    load();
  }, []);

  const fmt = (n) => `$${(+n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  const fmtTokens = (n) => `${(+n / 1_000_000).toFixed(1)}M`;

  const tenants = data?.tenants || [];
  // Filter pollution: drop tenants below $1 OR with <0.5% share — keeps the
  // chart on-brand even when pytest seeds or one-off attributions pile up.
  // Recompute totals + percent_share over the kept set.
  const significantTenants = (() => {
    const kept = tenants.filter((t) => t.cost_usd >= 1.0 || t.percent_share >= 0.5);
    const subtotal = kept.reduce((acc, t) => acc + t.cost_usd, 0);
    return kept.map((t) => ({
      ...t,
      percent_share: subtotal > 0 ? Math.round((t.cost_usd / subtotal) * 1000) / 10 : 0,
    }));
  })();

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="virtual-tenant-page">
      <Helmet><title>Virtual Tenant Attribution · CARTIEAI</title></Helmet>
      {!user && <PublicNavbar />}

      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 relative">
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 rounded-md px-2 py-1 mb-5 bg-emerald-500/10">
            <Users className="w-3 h-3" /> Instant departmental chargebacks
          </div>
          {PROXY_WIRED_BADGE}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="vt-h1">
            One shared model.<br /><span className="text-emerald-400">Five P&Ls.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
            You buy one dedicated Claude 4.5 or GPT-5 instance. Engineering sees one giant $50k bill. Finance asks: <em>"Which department is using this?"</em>
          </p>
          <p className="mt-3 text-sm text-slate-400">
            Pass a single header — <code className="text-emerald-300 font-mono bg-slate-900 px-1.5 py-0.5 rounded">X-Cartie-Tenant: marketing</code> — on every proxy call. CARTIE auto-splits the shared bill into a per-department P&L for end-of-month chargebacks.
            <br />
            <Link to="/sdk" className="text-emerald-400 hover:text-emerald-300 underline mt-2 inline-flex items-center gap-1" data-testid="vt-sdk-link">
              Or skip the header — use the Cartie SDK auto-injection →
            </Link>
          </p>
        </div>

        {/* IT TAKES ONE HEADER */}
        <div className="mt-10 grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300 mb-2">
              <Code2 className="w-3 h-3 inline mr-1" /> One header. That's it.
            </div>
            <pre className="rounded-xl border border-emerald-500/20 bg-slate-950/80 p-4 text-[12px] leading-relaxed font-mono text-emerald-200 overflow-x-auto whitespace-pre shadow-lg shadow-slate-900/40" data-testid="vt-sample-curl">
{SAMPLE_CURL}
            </pre>
            <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
              No new SDK. No code changes beyond one header. Works with the OpenAI SDK, LangChain, LlamaIndex, and direct curl.
            </p>
          </div>

          {/* LIVE BREAKDOWN */}
          <div className="lg:col-span-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300">
                <PieIcon className="w-3 h-3 inline mr-1" /> Live breakdown — month {data?.month || '…'}
              </div>
              <div className="flex gap-2">
                <button onClick={load} className="text-[11px] font-mono uppercase tracking-widest text-slate-400 hover:text-emerald-300 inline-flex items-center gap-1" data-testid="vt-refresh">
                  <RefreshCcw className="w-3 h-3" /> Refresh
                </button>
                <button onClick={seed} disabled={seeding} className="text-[11px] font-mono uppercase tracking-widest text-amber-300 hover:text-amber-200 inline-flex items-center gap-1" data-testid="vt-seed">
                  {seeding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Seed demo
                </button>
              </div>
            </div>

            {loading ? (
              <div className="h-72 flex items-center justify-center rounded-xl border border-dashed border-white/10 bg-slate-900/40">
                <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
              </div>
            ) : significantTenants.length === 0 ? (
              <div className="rounded-xl border border-dashed border-white/10 bg-slate-900/40 p-8 text-center" data-testid="vt-empty">
                <Database className="w-10 h-10 mx-auto text-slate-600 mb-3" />
                <p className="text-sm text-slate-400">No attributed calls yet. Click <strong>Seed demo</strong> above to load a 60-day sample dataset.</p>
              </div>
            ) : (
              <>
                {/* Stacked bar */}
                <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5 mb-3" data-testid="vt-stack-bar">
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="text-sm font-semibold text-slate-300">Total shared spend</div>
                    <div className="text-2xl font-extrabold font-mono text-white" data-testid="vt-total">{fmt(significantTenants.reduce((a, t) => a + t.cost_usd, 0))}</div>
                  </div>
                  <div className="flex h-8 rounded-md overflow-hidden border border-white/10">
                    {significantTenants.map((t, i) => (
                      <div
                        key={t.tenant_label}
                        style={{ backgroundColor: TENANT_COLORS[i % TENANT_COLORS.length], width: `${Math.max(t.percent_share, 2)}%` }}
                        className="flex items-center justify-center text-[11px] font-bold text-slate-950"
                      >
                        {t.percent_share > 8 && `${t.percent_share}%`}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-mono">
                    {significantTenants.map((t, i) => (
                      <div key={t.tenant_label} className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: TENANT_COLORS[i % TENANT_COLORS.length] }} />
                        <span className="text-slate-300">{t.tenant_label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-xl border border-white/10 bg-slate-900/60" data-testid="vt-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                        <th className="text-left p-3">Department</th>
                        <th className="text-right p-3">Spend</th>
                        <th className="text-right p-3">Share</th>
                        <th className="text-right p-3">Calls</th>
                        <th className="text-right p-3">Tokens</th>
                        <th className="text-left p-3">Models</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {significantTenants.map((t, i) => (
                        <tr key={t.tenant_label} className="border-t border-white/5 hover:bg-emerald-500/5 transition-colors" data-testid={`vt-row-${t.tenant_label}`}>
                          <td className="p-3 font-semibold text-white flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: TENANT_COLORS[i % TENANT_COLORS.length] }} />
                            {t.tenant_label}
                          </td>
                          <td className="p-3 text-right font-mono text-emerald-300 font-bold">{fmt(t.cost_usd)}</td>
                          <td className="p-3 text-right font-mono">{t.percent_share}%</td>
                          <td className="p-3 text-right font-mono text-slate-400">{t.calls.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-slate-400">{fmtTokens(t.input_tokens + t.output_tokens)}</td>
                          <td className="p-3 text-[11px] font-mono text-slate-400">{t.models_used.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        {/* WHY IT MATTERS */}
        <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="vt-benefits">
          {[
            { title: 'No code changes', desc: 'One HTTP header. Compatible with OpenAI SDK, LangChain, LlamaIndex, raw curl.', Icon: Code2 },
            { title: 'Instant chargebacks', desc: 'End-of-month invoice to each department. No more "we paid $50k, no idea who used it."', Icon: ListChecks },
            { title: 'Anomaly attribution', desc: 'When token spend spikes, you immediately see WHICH department caused it.', Icon: Sparkles },
          ].map((b) => {
            const Ic = b.Icon;
            return (
              <div key={b.title} className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 mb-2">
                  <Ic className="w-4 h-4 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <div className="text-sm font-bold text-white">{b.title}</div>
                <div className="text-xs text-slate-400 mt-1 leading-relaxed">{b.desc}</div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 text-center shadow-lg shadow-emerald-500/10" data-testid="vt-cta">
          <h3 className="text-2xl font-extrabold text-white tracking-tight">Stop fighting over the shared bill.</h3>
          <p className="text-slate-300 mt-2 max-w-xl mx-auto">14-day pilot · no card · we ship a proxy endpoint that already speaks the header and lights up your dashboard within minutes.</p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/signup" className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/20 transition-all" data-testid="vt-signup">
              Start free pilot <ArrowRight className="w-4 h-4 inline ml-1" />
            </Link>
            <Link to="/patent-portfolio" className="px-6 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500 hover:text-emerald-300 text-slate-300 text-sm font-semibold transition-all">
              See all {ENGINE_COUNT} engines
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualTenant;
